import { useEffect, useMemo, useState } from 'react'
import { loadRoadmaps, login } from './api'
import { columns, itemsForColumn } from './roadmapColumns'
import type { AppRoadmap, RoadmapItem } from './types'
import './styles.css'

const VERSION = '0.1.4'

function requestedSlug() {
  const match = window.location.pathname.match(/^\/app\/([^/]+)/)
  return match ? decodeURIComponent(match[1]) : undefined
}

function Item({ item }: { item: RoadmapItem }) {
  return <article className="item">
    <div className="item-top"><strong>{item.id}</strong><span className={`priority ${item.priority?.toLowerCase() || 'normal'}`}>{item.priority || 'Normal'}</span></div>
    <h3>{item.title}</h3>
    {item.instruction && <p>{item.instruction}</p>}
    {item.date && <time>{new Intl.DateTimeFormat('da-DK').format(new Date(item.date))}</time>}
  </article>
}

function AppBoard({ app }: { app: AppRoadmap }) {
  return <section className="app-board">
    <header className="app-header">
      <div><span className="eyebrow">{app.project}</span><h2>{app.name}</h2></div>
      <div className="app-meta"><span>Version {app.version || '–'}</span><span className={`deploy ${app.deploymentStatus || 'unknown'}`}>{app.deploymentStatus || 'ukendt'}</span></div>
    </header>
    <div className="columns">
      {columns.map(column => {
        const items = itemsForColumn(app.items, column)
        return <section className="column" key={column.title}><h3>{column.title}<span>{items.length}</span></h3>
          <div className="items">{items.length ? items.map(item => <Item item={item} key={item.id} />) : <p className="empty">Ingen tiltag</p>}</div>
        </section>
      })}
    </div>
    <footer>Opdateret {new Intl.DateTimeFormat('da-DK', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(app.updatedAt))}</footer>
  </section>
}

export default function App() {
  const slug = useMemo(requestedSlug, [])
  const [apps, setApps] = useState<AppRoadmap[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [online, setOnline] = useState(navigator.onLine)
  const returnUrl = slug ? apps[0]?.returnUrl : undefined

  async function handleLogin(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    try {
      await login(String(data.get('email') || ''), String(data.get('password') || ''))
      setError(''); setLoading(true)
      const result = await loadRoadmaps(slug); setApps(result.apps)
    } catch (err) { setError(err instanceof Error ? err.message : 'Login mislykkedes.') }
    finally { setLoading(false) }
  }

  useEffect(() => {
    const update = () => setOnline(navigator.onLine)
    window.addEventListener('online', update); window.addEventListener('offline', update)
    loadRoadmaps(slug).then(result => setApps(result.apps)).catch(err => setError(err instanceof Error ? err.message : 'Ukendt fejl')).finally(() => setLoading(false))
    return () => { window.removeEventListener('online', update); window.removeEventListener('offline', update) }
  }, [slug])

  return <main>
    <header className="topbar"><div>{returnUrl && <a className="back-button" href={returnUrl} aria-label="Tilbage til appen"><span aria-hidden="true">←</span><span>Tilbage</span></a>}<span className="brand-mark">RM</span><div><h1>Roadmap</h1><p>{slug ? 'App-roadmap' : 'Samlet statusboard'}</p></div></div><div className="system"><span className={online ? 'online' : 'offline'}>{online ? 'Online' : 'Offline'}</span><span>V{VERSION}</span></div></header>
    <div className="content">
      {loading && <div className="message">Henter roadmap…</div>}
      {error && !slug && <form className="login" onSubmit={handleLogin}><span className="eyebrow">Ejeradgang</span><h2>Log ind</h2><label>E-mail<input name="email" type="email" autoComplete="username" required /></label><label>Adgangskode<input name="password" type="password" autoComplete="current-password" required /></label><button type="submit">Log ind</button><p>{error}</p></form>}
      {error && slug && <div className="message error"><strong>Roadmap kunne ikke vises</strong><span>{error}</span></div>}
      {!loading && !error && apps.length === 0 && <div className="message">Ingen apps er tilkoblet endnu.</div>}
      {apps.map(app => <AppBoard app={app} key={app.slug} />)}
    </div>
  </main>
}
